import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Radio, RefreshCw, ChevronDown, ChevronRight, Loader2, CheckCircle2, XCircle, MinusCircle, Clock } from "lucide-react";

const EV_STATUS = {
  done: { cls: "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30", Icon: CheckCircle2 },
  pending: { cls: "bg-amber-500/15 text-amber-300 ring-amber-500/30", Icon: Clock },
  processing: { cls: "bg-sky-500/15 text-sky-300 ring-sky-500/30", Icon: Loader2 },
  failed: { cls: "bg-rose-500/15 text-rose-300 ring-rose-500/30", Icon: XCircle },
  skipped: { cls: "bg-slate-500/15 text-slate-300 ring-slate-500/30", Icon: MinusCircle },
};
const DEL_STATUS = {
  sent: "text-emerald-300", failed: "text-rose-300", skipped: "text-slate-400",
};
const fmt = (s) => (s ? new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

export default function PlatformEvents() {
  const [companies, setCompanies] = useState([]);
  const [cid, setCid] = useState("");
  const [events, setEvents] = useState(null);
  const [open, setOpen] = useState(null);
  const [draining, setDraining] = useState(false);

  useEffect(() => {
    api.get("/platform/companies").then((r) => {
      const list = r.data.companies || [];
      setCompanies(list);
      if (list.length) setCid(list[0].id);
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!cid) return;
    setEvents(null);
    api.get(`/platform/companies/${cid}/events`).then((r) => setEvents(r.data.events || [])).catch(() => setEvents([]));
  }, [cid]);
  useEffect(() => { load(); }, [load]);

  const drain = async () => {
    if (!cid) return;
    setDraining(true);
    try {
      const r = await api.post(`/platform/companies/${cid}/events/drain`);
      toast.success(`Retried ${r.data.processed} due event${r.data.processed === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not retry"); }
    finally { setDraining(false); }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="platform-events-page">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <span className="w-10 h-10 rounded-xl bg-emerald-500/15 text-emerald-300 flex items-center justify-center ring-1 ring-emerald-500/30">
          <Radio className="w-5 h-5" />
        </span>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold tracking-tight">Comms Activity</h1>
          <p className="text-sm text-slate-400">Emitted operational events and who was notified on each channel.</p>
        </div>
        <select
          value={cid}
          onChange={(e) => setCid(e.target.value)}
          data-testid="events-company-select"
          className="h-10 rounded-lg bg-slate-900 border border-white/10 text-sm text-slate-100 px-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
        >
          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <button onClick={drain} disabled={draining || !cid} data-testid="events-drain-btn"
          className="h-10 inline-flex items-center gap-2 rounded-lg bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30 px-3.5 text-sm font-medium hover:bg-emerald-500/25 disabled:opacity-40 transition-colors">
          {draining ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Retry due
        </button>
      </div>

      {events === null ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-10"><Loader2 className="w-4 h-4 animate-spin" /> Loading events…</div>
      ) : events.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/10 bg-slate-900/40 p-12 text-center text-slate-400" data-testid="events-empty">
          No events emitted yet for this company. Complete a job (with a wired trigger) to see activity here.
        </div>
      ) : (
        <div className="rounded-2xl ring-1 ring-white/10 bg-slate-900/60 overflow-hidden" data-testid="events-table">
          <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 border-b border-white/5">
            <div className="col-span-3">Event</div>
            <div className="col-span-3">Entity</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2">Attempts</div>
            <div className="col-span-2">When</div>
          </div>
          <div className="divide-y divide-white/5">
            {events.map((ev) => {
              const st = EV_STATUS[ev.status] || EV_STATUS.skipped;
              const isOpen = open === ev.id;
              return (
                <div key={ev.id} data-testid={`event-row-${ev.id}`}>
                  <button onClick={() => setOpen(isOpen ? null : ev.id)}
                    className="w-full grid grid-cols-12 gap-3 px-4 py-3 text-left hover:bg-white/5 items-center">
                    <div className="col-span-12 md:col-span-3 flex items-center gap-2 min-w-0">
                      {isOpen ? <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />}
                      <span className="font-medium text-sm text-slate-100 truncate">{ev.event_type}</span>
                    </div>
                    <div className="col-span-6 md:col-span-3 text-sm text-slate-400 truncate">{ev.entity_type} · {ev.entity_id || "—"}</div>
                    <div className="col-span-3 md:col-span-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${st.cls}`} data-testid={`event-status-${ev.id}`}>
                        <st.Icon className={`w-3 h-3 ${ev.status === "processing" ? "animate-spin" : ""}`} /> {ev.status}
                      </span>
                    </div>
                    <div className="col-span-3 md:col-span-2 text-sm text-slate-400 tabular-nums">{ev.attempts}</div>
                    <div className="col-span-12 md:col-span-2 text-xs text-slate-500">{fmt(ev.occurred_at)}</div>
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 pt-1 bg-slate-950/40" data-testid={`event-detail-${ev.id}`}>
                      {ev.last_error && <div className="mb-2 text-xs text-rose-300">Last error: {ev.last_error}{ev.next_retry_at ? ` · next retry ${fmt(ev.next_retry_at)}` : ""}</div>}
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Deliveries</div>
                      {(ev.deliveries || []).length === 0 ? (
                        <div className="text-sm text-slate-500">No delivery attempts recorded.</div>
                      ) : (
                        <div className="space-y-1">
                          {ev.deliveries.map((d, i) => (
                            <div key={i} className="flex items-center gap-3 text-sm rounded-lg bg-white/5 px-3 py-1.5" data-testid={`event-delivery-${ev.id}-${i}`}>
                              <span className="w-16 shrink-0 text-xs uppercase tracking-wide text-slate-400">{d.channel}</span>
                              <span className="min-w-0 flex-1 truncate text-slate-200">{d.recipient_key}</span>
                              <span className={`text-xs font-semibold ${DEL_STATUS[d.status] || "text-slate-400"}`}>{d.status}</span>
                              {d.last_error && <span className="text-[11px] text-rose-300/80 truncate max-w-[180px]">{d.last_error}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
